/**
 * Render placement for muscle highlight capsules (visual only — which muscles and roles
 * a pose uses still comes from MUSCLE_CATALOG + pose.muscles).
 *
 * Each capsule sits on a bone segment (bone → child bone) at fraction `t`, offset in the bone's
 * anatomical frame: `ant` = anterior (+) / posterior (−), `med` = toward body midline (+) / lateral (−),
 * `up` = along the segment. Rig axes (Vitruvian Mixamo): legs & spine local Z = anterior;
 * upper arm local X = posterior (left) / anterior (right); leg local X = medial (left) / lateral (right).
 */
export interface Placement {
  bone: string // without side prefix for limbs, e.g. 'UpLeg'
  to: string
  limb: 'leg' | 'arm' | 'axial'
  t: number
  ant?: number
  med?: number
  radius: number
  length: number // fraction of segment length
  pair?: boolean // axial: mirror left/right with `med` as lateral distance
}

export const MUSCLE_PLACEMENT: Record<string, Placement[]> = {
  quads: [{ bone: 'UpLeg', to: 'Leg', limb: 'leg', t: 0.52, ant: 0.05, radius: 0.057, length: 0.62 }],
  hamstrings: [{ bone: 'UpLeg', to: 'Leg', limb: 'leg', t: 0.52, ant: -0.05, radius: 0.053, length: 0.6 }],
  adductors: [{ bone: 'UpLeg', to: 'Leg', limb: 'leg', t: 0.32, med: 0.05, radius: 0.046, length: 0.42 }],
  abductors: [{ bone: 'UpLeg', to: 'Leg', limb: 'leg', t: 0.12, med: -0.062, radius: 0.05, length: 0.22 }],
  glutes: [{ bone: 'UpLeg', to: 'Leg', limb: 'leg', t: 0.02, ant: -0.088, med: -0.013, radius: 0.072, length: 0.16 }],
  calves: [{ bone: 'Leg', to: 'Foot', limb: 'leg', t: 0.32, ant: -0.044, radius: 0.046, length: 0.42 }],
  soleus: [{ bone: 'Leg', to: 'Foot', limb: 'leg', t: 0.62, ant: -0.035, radius: 0.037, length: 0.3 }],
  erectorSpinae: [{ bone: 'Spine', to: 'Neck', limb: 'axial', t: 0.45, ant: -0.094, med: 0.037, radius: 0.033, length: 0.72, pair: true }],
  abs: [{ bone: 'Spine', to: 'Spine2', limb: 'axial', t: 0.4, ant: 0.106, radius: 0.066, length: 1.0 }],
  pecs: [{ bone: 'Spine2', to: 'Neck', limb: 'axial', t: 0.25, ant: 0.106, med: 0.081, radius: 0.05, length: 0.45, pair: true }],
  traps: [{ bone: 'Spine2', to: 'Neck', limb: 'axial', t: 0.8, ant: -0.069, med: 0.062, radius: 0.042, length: 0.5, pair: true }],
  deltoids: [{ bone: 'Arm', to: 'ForeArm', limb: 'arm', t: 0.1, med: -0.015, radius: 0.05, length: 0.3 }],
  biceps: [{ bone: 'Arm', to: 'ForeArm', limb: 'arm', t: 0.55, ant: 0.033, radius: 0.033, length: 0.55 }],
  triceps: [{ bone: 'Arm', to: 'ForeArm', limb: 'arm', t: 0.52, ant: -0.035, radius: 0.035, length: 0.6 }],
}
